import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Box,
  Typography,
  Skeleton,
  Tooltip,
  Collapse,
  Badge,
  Button,
} from '@mui/material';
import { useResizableColumns } from './hooks/useResizableColumns';
import ResizableTableCell from './ResizableTableCell';
import ResizableSortableHeader from './ResizableSortableHeader';
import ColumnResizeHandle from './ColumnResizeHandle';
import { useDragDrop } from './FileCabinet/DragDropProvider';
import {
  MoreVert as MoreVertIcon,
  GetApp as DownloadIcon,
  Visibility as ViewIcon,
  Share as ShareIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Description as DescriptionIcon,
  Flag as FlagIcon,
  OutlinedFlag as OutlinedFlagIcon,
  CreateNewFolder as AddToFolderIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { 
  toggleDocumentFlag, 
  selectIsDocumentFlagged,
  toggleSupplementalDocumentFlag,
  selectIsSupplementalDocumentFlagged 
} from 'redux/flaggedItemsSlice';
import { loggedInUserState } from 'redux/userSlice';
import StatusIndicator from 'components/shared/StatusIndicator';
import './styles/DocHubTable.scss';

// Styled badge for document count
const DocumentCountBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: -25,
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: "0.65rem",
    minWidth: 16,
    height: 16,
    padding: "0 4px",
    backgroundColor: theme.palette.primary.main,
  },
}));

const DocHubTable = ({ documents, loading, selectedColumns, sortBy, sortAsc, setSortBy, setSortAsc, onNavigateToDocument, onNavigateToVendor, onShareDocument, onDownloadDocument, onAddToFolder, onDeleteDocument, documentFolderPaths = {}, onFolderPathClick }) => {
  const dispatch = useDispatch();
  const { startDrag, endDrag, isDragging } = useDragDrop();
  const loggedInUser = useSelector(loggedInUserState);
  const flaggedDocumentIds = useSelector(state => state.flaggedItems?.documents?.map(doc => doc.id) || []);
  const flaggedSupplementalIds = useSelector(state => state.flaggedItems?.supplementalDocuments?.map(doc => doc.id) || []);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  
  // Format ID to show last 8 characters
  const formatDocId = (id) => {
    if (!id || id.length <= 8) return id;
    return `...${id.slice(-8)}`;
  };
  
  // Column configurations with increased widths to prevent overlapping
  const columnConfig = {
    id: { label: 'ID', width: '120px', sortable: true, resizable: true },
    name: { label: 'Document Name', width: '250px', sortable: true, resizable: true },
    type: { label: 'Type', width: '130px', sortable: true, resizable: true },
    category: { label: 'Category', width: '140px', sortable: true, resizable: true },
    status: { label: 'Status', width: '130px', sortable: true, resizable: true },
    subcontractor: { label: 'Subcontractor', width: '220px', sortable: true, resizable: true },
    uploaded: { label: 'Uploaded', width: '140px', sortable: true, resizable: true },
    lastModified: { label: 'Last Modified', width: '140px', sortable: true, resizable: true },
    expirationDate: { label: 'Expiration Date', width: '150px', sortable: true, resizable: true },
    folderPath: { label: 'Folder Path', width: '220px', sortable: false, resizable: true },
    actions: { label: 'Actions', width: '130px', sortable: false, resizable: false },
  };
  
  // Initialize resizable columns hook
  const {
    columnWidths,
    handleResizeStart,
    getColumnWidth,
    resetColumnWidths,
    containerRef
  } = useResizableColumns(loggedInUser?.id, columnConfig);
  
  // Toggle row expansion
  const toggleRowExpansion = (docId) => {
    setExpandedRows(prev => ({
      ...prev,
      [docId]: !prev[docId]
    }));
  };
  
  // Document type color mapping
  const getTypeColor = (type) => {
    const typeColors = {
      'certificate': 'primary',
      'policy': 'secondary',
      'endorsement': 'info',
      'document': 'default',
    };
    return typeColors[type] || 'default';
  };
  
  // Handle menu open
  const handleMenuOpen = (event, doc) => {
    setAnchorEl(event.currentTarget);
    setSelectedDoc(doc);
  };
  
  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDoc(null);
  };
  
  // Handle document actions
  const handleAction = (action) => {
    if (!selectedDoc) return;
    
    switch (action) {
      case 'view':
        onNavigateToDocument(selectedDoc.id);
        break;
      case 'download':
        if (onDownloadDocument) {
          onDownloadDocument(selectedDoc);
        }
        break;
      case 'share':
        if (onShareDocument && selectedDoc.type === 'certificate') {
          onShareDocument(selectedDoc);
        }
        break;
      case 'addToFolder':
        if (onAddToFolder) {
          onAddToFolder(selectedDoc);
        }
        break;
      case 'delete':
        if (onDeleteDocument) {
          onDeleteDocument(selectedDoc);
        }
        break;
      default:
        break;
    }
    
    handleMenuClose();
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'MM/dd/yyyy');
    } catch (error) {
      return '-';
    }
  };
  
  // Calculate minimum table width based on column widths
  const minTableWidth = selectedColumns.reduce((sum, col) => {
    return sum + getColumnWidth(col);
  }, 0);
  
  // Render loading skeleton
  if (loading) {
    return (
      <TableContainer ref={containerRef} className="dochub-table-container compact-table">
        <Table className="dochub-table" sx={{ minWidth: `${minTableWidth}px` }}>
          <TableHead>
            <TableRow>
              {selectedColumns.map(col => {
                const config = columnConfig[col];
                if (!config) return null;
                
                if (config.sortable && col !== 'actions') {
                  return (
                    <ResizableSortableHeader
                      key={col}
                      sortId={col}
                      label={config.label}
                      sortBy={sortBy}
                      sortAsc={sortAsc}
                      setSortBy={setSortBy}
                      setSortAsc={setSortAsc}
                      width={getColumnWidth(col)}
                      resizable={config.resizable !== false}
                      onResizeStart={handleResizeStart}
                    />
                  );
                } else {
                  return (
                    <ResizableTableCell
                      key={col}
                      columnKey={col}
                      width={getColumnWidth(col)}
                      onResizeStart={handleResizeStart}
                      resizable={config.resizable !== false}
                      isSticky={col === 'actions'}
                    >
                      {config.label}
                    </ResizableTableCell>
                  );
                }
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {[...Array(10)].map((_, index) => (
              <TableRow key={index}>
                {selectedColumns.map(col => (
                  <TableCell key={col}>
                    <Skeleton 
                      animation="wave" 
                      height={30}
                      sx={{ 
                        transform: 'scale(1, 0.8)',
                        my: 0.5 
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
  
  // Render empty state
  if (!documents || documents.length === 0) {
    return (
      <Box className="dochub-empty-state">
        <Typography variant="h6" color="text.secondary">
          No documents found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try adjusting your filters or search terms
        </Typography>
      </Box>
    );
  }
  
  return (
    <>
      <TableContainer ref={containerRef} className="dochub-table-container compact-table">
        <Table className="dochub-table" stickyHeader sx={{ minWidth: `${minTableWidth}px` }}>
          <TableHead>
            <TableRow>
              {selectedColumns.map(col => {
                const config = columnConfig[col];
                if (!config) return null;
                
                if (config.sortable && col !== 'actions') {
                  return (
                    <ResizableSortableHeader
                      key={col}
                      sortId={col}
                      label={config.label}
                      sortBy={sortBy}
                      sortAsc={sortAsc}
                      setSortBy={setSortBy}
                      setSortAsc={setSortAsc}
                      width={getColumnWidth(col)}
                      resizable={config.resizable !== false}
                      onResizeStart={handleResizeStart}
                      className="dochub-table-header"
                    />
                  );
                } else {
                  return (
                    <ResizableTableCell
                      key={col}
                      columnKey={col}
                      width={getColumnWidth(col)}
                      onResizeStart={handleResizeStart}
                      resizable={config.resizable !== false}
                      className="dochub-table-header"
                      isSticky={col === 'actions'}
                    >
                      {config.label}
                    </ResizableTableCell>
                  );
                }
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((doc) => {
              const isFlagged = flaggedDocumentIds.includes(doc.id);
              
              const handleDragStart = (e) => {
                startDrag(doc, 'document');
                e.dataTransfer.effectAllowed = 'copy';
                // Add visual feedback
                e.currentTarget.style.opacity = '0.5';
              };
              
              const handleDragEnd = (e) => {
                endDrag();
                // Remove visual feedback
                e.currentTarget.style.opacity = '';
              };
              
              return (
              <React.Fragment key={doc.id}>
                <TableRow 
                  hover
                  draggable
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  sx={{
                    cursor: isDragging ? 'grabbing' : 'grab',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    }
                  }}>
                  {selectedColumns.includes('id') && (
                    <TableCell sx={{ width: `${getColumnWidth('id')}px`, maxWidth: `${getColumnWidth('id')}px` }}>
                      <Tooltip title={doc.id} placement="top">
                        <Typography variant="body2" className="dochub-id">
                          {formatDocId(doc.id)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  )}
                  
                  {selectedColumns.includes('name') && (
                    <TableCell sx={{ width: `${getColumnWidth('name')}px`, maxWidth: `${getColumnWidth('name')}px` }}>
                      <Typography
                        variant="body2"
                        onClick={() => onNavigateToDocument(doc.id)}
                        className="dochub-name-text"
                        component="span"
                      >
                        {doc.name}
                      </Typography>
                    </TableCell>
                  )}
                  
                  {selectedColumns.includes('type') && (
                    <TableCell sx={{ width: `${getColumnWidth('type')}px`, maxWidth: `${getColumnWidth('type')}px` }}>
                      <Chip
                        label={doc.type}
                        size="small"
                        color={getTypeColor(doc.type)}
                        className="dochub-type-chip"
                      />
                    </TableCell>
                  )}
                  
                  {selectedColumns.includes('category') && (
                    <TableCell sx={{ width: `${getColumnWidth('category')}px`, maxWidth: `${getColumnWidth('category')}px` }}>
                      <Typography variant="body2">
                        {doc.category || '-'}
                      </Typography>
                    </TableCell>
                  )}
                  
                  {selectedColumns.includes('status') && (
                    <TableCell sx={{ width: `${getColumnWidth('status')}px`, maxWidth: `${getColumnWidth('status')}px` }}>
                      <StatusIndicator label={doc.status} size="small" />
                    </TableCell>
                  )}
                  
                  {selectedColumns.includes('subcontractor') && (
                    <TableCell sx={{ 
                      width: `${getColumnWidth('subcontractor')}px`, 
                      maxWidth: `${getColumnWidth('subcontractor')}px`,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      <Typography 
                        variant="body2"
                        onClick={() => onNavigateToVendor && doc.clientVendorId && onNavigateToVendor(doc.vendorId, doc.clientVendorId)}
                        sx={{
                          cursor: doc.clientVendorId && doc.subcontractor !== '-' ? 'pointer' : 'default',
                          color: doc.clientVendorId && doc.subcontractor !== '-' ? '#1976d2' : 'inherit',
                          '&:hover': {
                            textDecoration: doc.clientVendorId && doc.subcontractor !== '-' ? 'underline' : 'none'
                          }
                        }}
                      >
                        {doc.subcontractor}
                      </Typography>
                    </TableCell>
                  )}
                  
                  {selectedColumns.includes('uploaded') && (
                    <TableCell sx={{ width: `${getColumnWidth('uploaded')}px`, maxWidth: `${getColumnWidth('uploaded')}px` }}>
                      <Typography variant="body2">
                        {formatDate(doc.uploaded)}
                      </Typography>
                    </TableCell>
                  )}
                  
                  {selectedColumns.includes('lastModified') && (
                    <TableCell sx={{ width: `${getColumnWidth('lastModified')}px`, maxWidth: `${getColumnWidth('lastModified')}px` }}>
                      <Typography variant="body2">
                        {formatDate(doc.lastModified)}
                      </Typography>
                    </TableCell>
                  )}
                  
                  {selectedColumns.includes('expirationDate') && (
                    <TableCell sx={{ width: `${getColumnWidth('expirationDate')}px`, maxWidth: `${getColumnWidth('expirationDate')}px` }}>
                      <Typography 
                        variant="body2"
                        className={doc.expirationDate && new Date(doc.expirationDate) < new Date() ? 'expired-date' : ''}
                      >
                        {formatDate(doc.expirationDate)}
                      </Typography>
                    </TableCell>
                  )}
                  
                  {selectedColumns.includes('folderPath') && (
                    <TableCell sx={{ width: `${getColumnWidth('folderPath')}px`, maxWidth: `${getColumnWidth('folderPath')}px` }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {(() => {
                          const folderPaths = documentFolderPaths[doc.id] || [];
                          
                          if (folderPaths.length === 0) {
                            return (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            );
                          }
                          
                          return folderPaths.map((folder, index) => (
                            <Typography
                              key={folder.folderID}
                              variant="body2"
                              onClick={() => onFolderPathClick && onFolderPathClick(folder.folderID, folder.folderName)}
                              sx={{
                                cursor: 'pointer',
                                color: 'primary.main',
                                textDecoration: 'none',
                                fontSize: '0.75rem',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }}
                              title={`Click to navigate to folder: ${folder.folderPath}`}
                            >
                              {folder.folderPath}
                            </Typography>
                          ));
                        })()}
                      </Box>
                    </TableCell>
                  )}
                  
                  {selectedColumns.includes('actions') && (
                    <TableCell sx={{ 
                      width: `${getColumnWidth('actions')}px`, 
                      minWidth: `${getColumnWidth('actions')}px`,
                      position: 'sticky',
                      right: 0,
                      backgroundColor: 'background.paper',
                      zIndex: 1,
                      boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={isFlagged ? "Remove flag" : "Flag this document"}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              dispatch(toggleDocumentFlag({
                                document: doc,
                                flaggedBy: loggedInUser?.email || loggedInUser?.id
                              }));
                            }}
                            color={isFlagged ? "error" : "default"}
                          >
                            {isFlagged ? <FlagIcon /> : <OutlinedFlagIcon />}
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, doc)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                        {doc.relatedDocuments?.length > 0 && (
                          <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => toggleRowExpansion(doc.id)}
                            sx={{
                              border: "1px solid",
                              borderColor: "grey.300",
                              borderRadius: "50%",
                              width: 32,
                              height: 32,
                              bgcolor: "background.paper",
                              "&:hover": {
                                bgcolor: "grey.100",
                              },
                            }}
                          >
                            <DocumentCountBadge badgeContent={doc.relatedDocuments.length} color="primary">
                              {expandedRows[doc.id] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </DocumentCountBadge>
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
                
                {/* Expandable row for related documents */}
                {doc.relatedDocuments?.length > 0 && expandedRows[doc.id] && (
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={selectedColumns.length}>
                      <Collapse in={expandedRows[doc.id]} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1, marginTop: 0.5 }} className="related-documents-section">
                          <Typography variant="h6" gutterBottom component="div" sx={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 0.5 }}>
                            Related Documents
                          </Typography>
                          <Table size="small" aria-label="related-documents">
                            <TableHead>
                              <TableRow>
                                <TableCell>Document Name</TableCell>
                                <TableCell align="right">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {doc.relatedDocuments.map((relatedDoc) => {
                                const isSupplementalFlagged = flaggedSupplementalIds.includes(relatedDoc.id);
                                return (
                                <TableRow key={relatedDoc.id}>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <DescriptionIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                                      <Typography variant="body2">{relatedDoc.name}</Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
                                      <Tooltip title={isSupplementalFlagged ? "Remove flag" : "Flag this document"}>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            dispatch(toggleSupplementalDocumentFlag({
                                              document: relatedDoc,
                                              parentCertificateId: doc.id,
                                              flaggedBy: loggedInUser?.email || loggedInUser?.id
                                            }));
                                          }}
                                          color={isSupplementalFlagged ? "error" : "default"}
                                        >
                                          {isSupplementalFlagged ? <FlagIcon fontSize="small" /> : <OutlinedFlagIcon fontSize="small" />}
                                        </IconButton>
                                      </Tooltip>
                                      <Button
                                        size="small"
                                        startIcon={<ViewIcon />}
                                        onClick={() => onNavigateToDocument(relatedDoc.id)}
                                      >
                                        View
                                      </Button>
                                      {onDeleteDocument && (
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => onDeleteDocument(relatedDoc)}
                                          title="Delete document"
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      )}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleAction('view')}>
          <ViewIcon fontSize="small" sx={{ mr: 1 }} />
          View
        </MenuItem>
        {onAddToFolder && (
          <MenuItem onClick={() => handleAction('addToFolder')}>
            <AddToFolderIcon fontSize="small" sx={{ mr: 1 }} />
            Add to Folder
          </MenuItem>
        )}
        <MenuItem onClick={() => handleAction('share')}>
          <ShareIcon fontSize="small" sx={{ mr: 1 }} />
          Share
        </MenuItem>
        {onDeleteDocument && (
          <MenuItem onClick={() => handleAction('delete')} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
            Delete
          </MenuItem>
        )}
        <MenuItem onClick={() => handleAction('download')}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          Download
        </MenuItem>
      </Menu>
    </>
  );
};

export default DocHubTable;